import { Observable } from 'rxjs';
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LoginComponent } from "./views/auth/login/login.component";
import { ForgotPasswordComponent } from "./views/auth/forgot-password/forgot-password.component";
import { NotFoundComponent } from "./views/errors/not-found/not-found.component";
import { ResetPasswordComponent } from "./views/auth/reset-password/reset-password.component";
import { RedirectAuthenticatedGuard } from "./core/guards/redirect-authenticated.guard";
import { DashboardComponent } from './layouts/dashboard/dashboard.component';
import {
  requireAccountingAccess, requireAdminAreaAccess, requireEstimateSettingsAccess, requireAdminOrgEventsAccess, requireAdminProjectsAccess,
  requireAuth, requireSuperadminAccess, requireBatteryCatalogAdminAccess, requireCustomerProfilesAdminAccess, requireFullAdminOrgAccess,
  requireProductionViewAccess, requireResellerEventsAccess, requireResellerWorkspaceAccess,
} from './core/auth/route-access.guard';
import { AdminDashboardComponent } from './standalone/admin-dashboard/admin-dashboard.component';

export const routes: Routes = [
  { path: 'questionnaire', loadChildren: () => import('./features/questionnaire/questionnaire.module').then(m => m.QuestionnaireModule) },
  { path: 'admin/alarm-limits', canActivate: [requireAuth, requireSuperadminAccess], loadChildren: () => import('./features/alarm-limits/alarm-limits.module').then(m => m.AlarmLimitsModule) },
  {
    path: '',
    pathMatch: 'full',
    canActivate: [RedirectAuthenticatedGuard],
    children: []
  },
  {
    path: 'login',
    component: LoginComponent
  },
  {
    path: 'reset-password',
    component: ForgotPasswordComponent
  },
  {
    path: 'reset-password/:token',
    component: ResetPasswordComponent
  },
  {
    path: 'projects',
    canActivate: [requireAuth],
    loadChildren: () => import('./features/projects/projects.module').then(m => m.ProjectsModule)
  },
  {
    path: 'support',
    canActivate: [requireAuth, requireResellerWorkspaceAccess],
    loadChildren: () => import('./features/support/support.module').then(m => m.SupportModule),
  },
  {
    path: 'users',
    canActivate: [requireAuth, requireResellerWorkspaceAccess],
    loadChildren: () => import('./features/users/users.module').then(m => m.UsersModule)
  },
  {
    path: 'profile',
    canActivate: [requireAuth],
    loadChildren: () => import('./features/profile/profile.module').then(m => m.ProfileModule)
  },
  {
    path: 'batteries/cabinet-models/create',
    pathMatch: 'full',
    redirectTo: 'admin/cabinets/models/create',
  },
  {
    path: 'batteries/cabinet-models/:id',
    pathMatch: 'full',
    redirectTo: 'admin/cabinets/models/:id',
  },
  {
    path: 'batteries/cabinet-models',
    pathMatch: 'full',
    redirectTo: 'admin/cabinets/models',
  },
  {
    path: 'batteries',
    redirectTo: 'admin/batteries'
  },
  {
    path: 'cabinets',
    redirectTo: 'admin/cabinets'
  },
  {
    path: 'events',
    canActivate: [requireAuth, requireResellerEventsAccess],
    loadChildren: () => import('./features/events/events.module').then(m => m.EventsModule)
  },
  {
    path: 'customer-profiles',
    canActivate: [requireAuth, requireResellerWorkspaceAccess],
    loadChildren: () => import('./features/customer-profiles/customer-profiles.module').then(m => m.CustomerProfilesModule)
  },
  {
    path: 'notifications',
    canActivate: [requireAuth],
    component: DashboardComponent,
    children: [
      {
        path: '',
        loadChildren: () => import('./features/notifications/notifications.module').then(m => m.NotificationsModule)
      }
    ]
  },
  {
    path: 'admin',
    canActivate: [requireAuth, requireAdminAreaAccess],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'projects',
      },
      {
        // Sales, support and superadmin; the dashboard gates its own cards and queues by capability.
        path: 'dashboard',
        canActivate: [requireAdminProjectsAccess],
        component: DashboardComponent,
        title: 'Admin dashboard',
        children: [
          {
            path: '',
            component: AdminDashboardComponent,
          }
        ]
      },
      {
        path: 'order-intakes',
        canActivate: [requireAdminProjectsAccess],
        component: DashboardComponent,
        children: [
          { path: '', loadComponent: () => import('./features/order-intakes/order-intakes.component').then(m => m.OrderIntakesComponent), title: 'Incoming orders' },
          { path: ':id', loadComponent: () => import('./features/order-intakes/order-intake-detail.component').then(m => m.OrderIntakeDetailComponent), canDeactivate: [(component: { canDeactivate: () => boolean | Observable<boolean> }) => component.canDeactivate()], runGuardsAndResolvers: 'paramsChange', title: 'Incoming order' },
        ],
      },
      {
        path: 'accounting',
        canActivate: [requireAccountingAccess],
        component: DashboardComponent,
        children: [{ path: '', loadComponent: () => import('./features/accounting/accounting.component').then(m => m.AccountingComponent) }],
      },
      {
        path: 'settings',
        canActivate: [requireEstimateSettingsAccess],
        component: DashboardComponent,
        children: [{ path: '', loadComponent: () => import('./features/admin-settings/admin-settings.component').then(m => m.AdminSettingsComponent), title: 'Admin settings' }],
      },
      {
        path: 'notification-settings',
        canActivate: [requireSuperadminAccess],
        component: DashboardComponent,
        children: [{ path: '', loadComponent: () => import('./features/notification-settings/notification-settings.component').then(m => m.NotificationSettingsComponent) }],
      },
      {
        path: 'resellers',
        canActivate: [requireFullAdminOrgAccess],
        loadChildren: () => import('./features/resellers/resellers.module').then(m => m.ResellersModule),
      },
      {
        path: 'users',
        canActivate: [requireFullAdminOrgAccess],
        loadChildren: () => import('./features/users/users.module').then(m => m.UsersModule),
      },
      {
        path: 'manufacturers',
        pathMatch: 'full',
        redirectTo: 'batteries/manufacturers',
      },
      {
        path: 'manufacturers/create',
        pathMatch: 'full',
        redirectTo: 'batteries/manufacturers/create',
      },
      {
        path: 'manufacturers/:id',
        pathMatch: 'full',
        redirectTo: 'batteries/manufacturers/:id',
      },
      {
        path: 'batteries/cabinet-models/create',
        pathMatch: 'full',
        redirectTo: 'cabinets/models/create',
      },
      {
        path: 'batteries/cabinet-models/:id',
        pathMatch: 'full',
        redirectTo: 'cabinets/models/:id',
      },
      {
        path: 'batteries/cabinet-models',
        pathMatch: 'full',
        redirectTo: 'cabinets/models',
      },
      {
        path: 'batteries',
        canActivate: [requireBatteryCatalogAdminAccess],
        loadChildren: () => import('./features/batteries/battery-models.module').then(m => m.BatteryModelsModule)
      },
      {
        path: 'cabinets',
        canActivate: [requireBatteryCatalogAdminAccess],
        loadChildren: () => import('./features/cabinets/cabinets.module').then(m => m.CabinetsModule)
      },
      {
        // /admin/cables
        path: 'cables',
        canActivate: [requireFullAdminOrgAccess],
        loadChildren: () => import('./features/cables/cables.module').then(m => m.CablesModule),
      },
      {
        path: 'projects',
        canActivate: [requireAdminProjectsAccess],
        loadChildren: () => import('./features/projects/projects.module').then(m => m.ProjectsModule),
      },
      {
        path: 'support',
        canActivate: [requireAdminProjectsAccess],
        loadChildren: () => import('./features/support/support.module').then(m => m.SupportModule),
      },
      {
        // /admin/production
        path: 'production',
        canActivate: [requireProductionViewAccess],
        loadChildren: () => import('./features/production/production.module').then(m => m.ProductionModule),
      },
      {
        path: 'customer-profiles',
        canActivate: [requireCustomerProfilesAdminAccess],
        loadChildren: () => import('./features/customer-profiles/customer-profiles.module').then(m => m.CustomerProfilesModule),
      },
      {
        path: 'events',
        canActivate: [requireAdminOrgEventsAccess],
        loadChildren: () => import('./features/events/events.module').then(m => m.EventsModule),
      },
      {
        path: 'notifications',
        canActivate: [requireAdminProjectsAccess],
        component: DashboardComponent,
        children: [
          {
            path: '',
            loadChildren: () => import('./features/notifications/notifications.module').then(m => m.NotificationsModule)
          }
        ]
      },
      {
        path: 'catalog',
        canActivate: [requireFullAdminOrgAccess],
        component: DashboardComponent,
        children: [
          { path: '', loadComponent: () => import('./features/catalog/items/catalog-items.component').then(m => m.CatalogItemsComponent), title: 'Master catalog' },
          { path: 'new', canActivate: [requireSuperadminAccess], loadComponent: () => import('./features/catalog/items/catalog-item-create.component').then(m => m.CatalogItemCreateComponent), title: 'Create catalog component' },
          { path: ':id', loadComponent: () => import('./features/catalog/items/catalog-item.component').then(m => m.CatalogItemComponent), title: 'Catalog item' },
        ],
      },
      {
        path: 'part-numbers',
        canActivate: [requireFullAdminOrgAccess],
        loadChildren: () => import('./features/parts-admin/parts-admin.module').then(m => m.PartsAdminModule)
      },
    ]
  },
  {
    path: '**',
    component: NotFoundComponent
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
